import React, {useEffect} from "react";
import {priceIdToString, priceTotal, Product, productIdOrdering} from "../types/products";
import {forcedSolutions, SolutionKey, SOLUTIONS} from "../types/solutions";
import {ENV_Coupons, ENVIRONMENT} from "../utils/constants";
import {navigate, Routes} from "../utils/router";
import {I18n, Translate} from "react-redux-i18n";
import {IMAGES, KButton, KBUTTON_VARIANT, KCard, KContainer, KSpace, KSpinner, KSpinnerSize} from "@kopjra/uikit";
import {Col, Row} from "react-bootstrap";
import {useParams} from "react-router-dom";

export interface StateProps {
    locale: string;
    products?: Product[];
}

export interface DispatchProps {
    onGetProducts: () => void;
}

export interface InnerProps {
}

export type Props = StateProps & DispatchProps & InnerProps;

export const Area: React.FC<Props> = ({products, onGetProducts}: Props) => {
    const { area } = useParams<{area: string}>();
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);
    useEffect(() => {
        onGetProducts();
    }, [onGetProducts]);

    const loadedSolutions = products ? Array.from(new Set(products.map((p) => p.solution))) : undefined;
    const solutions: SolutionKey[] = [...loadedSolutions || [], ...forcedSolutions.filter((s => !loadedSolutions?.includes(s as any)))] as any;

    const freePlanLinkMap: Map<string, string | null> = new Map<string, string>();
    function getFreePlanLink(solution: SolutionKey): string | null {
        if (freePlanLinkMap.has(solution)) {
            return freePlanLinkMap.get(solution) || null;
        } else {
            let link: string | null = Routes.SOLUTION_DETAIL(solution);
            if (products) {
                const solutionProducts = products.filter((p) => p.solution === solution && p.prices && p.prices.length);
                if (solutionProducts.length === 1) {
                    solutionProducts[0].prices.sort((a, b) => priceTotal(a) - priceTotal(b));
                    const zeroPrices = solutionProducts[0].prices.filter((p) => priceTotal(p) === 0);

                    if (zeroPrices.length === 0) {
                        const productId = solutionProducts[0].id;
                        if (ENV_Coupons[ENVIRONMENT][productId]) {
                            link = `${Routes.CART}?prices=${priceIdToString(solutionProducts[0].prices[0])}&codes=${ENV_Coupons[ENVIRONMENT][productId]}`;
                        } else {
                            link = null;
                        }
                    } else if (zeroPrices.length === 1) {
                        link = `${Routes.CART}?prices=${priceIdToString(zeroPrices[0])}`;
                    }
                } else {
                    const hasZeroPrice = solutionProducts.find((p) => p.prices?.find((pr) => priceTotal(pr) === 0));
                    if (!hasZeroPrice) {
                        link = null;
                    }
                }
            }
            freePlanLinkMap.set(solution, link);
            return link;
        }
    }

    function getSolutionSpaces(solutionKey: SolutionKey): number {
        const solution = SOLUTIONS[solutionKey];
        if (solution && solution.area === area) {
            let height = solution.customCTA?.length || 0;
            if (!solution.onlyCustomCTA) {
                height += 1; // for buy button
                if (solution.link) {
                    height ++;
                }
                if (getFreePlanLink(solutionKey)) {
                    height ++;
                }
            }
            return height * 5;
        } else {
            return 0;
        }
    }

    function getBiggestCard(): number {
        let maxHeight = 0;
        for (const solutionKey of solutions) {
            const height = getSolutionSpaces(solutionKey);
            if (height > maxHeight) {
                maxHeight = height;
            }
        }
        return maxHeight;
    }
    const maxCardHeight = getBiggestCard();

    function computeTailSpaces(s: SolutionKey): number {
        const height = getSolutionSpaces(s);
        return Math.max(maxCardHeight - height, 0);
    }

    return (
        <KContainer>
            <h5><img src={I18n.t(`areas.${area}.logo` as any)} height={85} alt={area}/></h5>
            <h4><Translate value={`areas.${area}.payoff` as any} dangerousHTML={true}/></h4>
            <KSpace spaces={3}/>
            {loadedSolutions ? (
                <Row>
                    {solutions.filter((s) => SOLUTIONS[s]?.area === area).sort((a, b) => productIdOrdering[a] < productIdOrdering[b] ? -1 : 1).map((s, index) => {
                        const freePlanLink = getFreePlanLink(s);
                        const hasCustomCTA = SOLUTIONS[s]?.customCTA && SOLUTIONS[s]?.customCTA?.length;
                        return (
                            <Col lg={4} key={index}>
                                <KCard fillHeight={true}>
                                    <h5><img src={SOLUTIONS[s]?.customLogo || IMAGES[s].still.large.default} height={70} alt={s}/></h5>
                                    <KSpace spaces={0.5}/>
                                    <h5><Translate value={`${s}.title`}/></h5>
                                    <h6><Translate value={`${s}.subtitle`}/></h6>
                                    <KSpace spaces={1.5}/>
                                    <p><Translate value={`${s}.description`} dangerousHTML={true}/></p>
                                    <div className="mt-auto">
                                        {SOLUTIONS[s]?.basePrice ? (
                                            <>
                                                <div className="from"><Translate value="generic.from"/></div>
                                                <div className="big-price">{SOLUTIONS[s]?.basePrice} €{SOLUTIONS[s]?.modality ? <div className="sub">/ <Translate  value={`generic.modality.${SOLUTIONS[s]?.modality}` as any}/></div> : null}</div>
                                                {SOLUTIONS[s]?.subtext ? <div className="from"><Translate value={SOLUTIONS[s]?.subtext || "" as any}/></div> : null}
                                                <KSpace spaces={1.5}/>
                                            </>
                                        ) : null}
                                        {!(SOLUTIONS[s]?.onlyCustomCTA) ? (
                                            <>
                                                <KButton id={"home-conf-buy"} text={<Translate value={"generic.confBuy"} />} onClick={() => navigate(Routes.SOLUTION_DETAIL(s), {})} fill={true}/>
                                                {freePlanLink && <KSpace spaces={1.5}/>}
                                                {freePlanLink &&  <KButton id={"home-free-plan"} text={<Translate value="generic.freePlan"/>} onClick={() => navigate(freePlanLink)} fill={true} variant={KBUTTON_VARIANT["outline-success"]}/>}
                                            </>
                                        ) : null}
                                        {!SOLUTIONS[s]?.onlyCustomCTA && hasCustomCTA ? <KSpace spaces={1.5}/> : null}
                                        {hasCustomCTA ? SOLUTIONS[s]?.customCTA?.map(
                                            (cta, index) => (
                                                <div key={index}>
                                                    <KButton id={"home-conf-buy"} variant={cta.variant || KBUTTON_VARIANT.primary} text={<Translate value={cta.key as any}/>} onClick={() =>  window.open(I18n.t(cta.link as any), "_blank")} fill={true}/>
                                                    {index < (SOLUTIONS[s]?.customCTA?.length || 0) - 1 ? <KSpace spaces={1.5}/> : null}
                                                </div>
                                            )
                                        ) : null}
                                        {SOLUTIONS[s]?.link ? (
                                            <>
                                                <KSpace spaces={3.5}/>
                                                <h6 style={{marginBottom: 0}}><a className="link" href={SOLUTIONS[s]?.link} target="_blank" rel="noopener noreferrer"><Translate value={"generic.access"}/><Translate value={`${s}.title`}/></a></h6>
                                            </>
                                        ) : null}
                                        {<KSpace spaces={computeTailSpaces(s)}/>}
                                    </div>
                                </KCard>
                            </Col>
                        )
                    })}
                </Row>
            ) : (
                <h1><KSpinner size={KSpinnerSize.nm}/></h1>
            )}
            <KSpace spaces={3}/>
            <h4><a className="link" href={I18n.t(`areas.${area}.estimatorLink` as any)} target="_blank" rel="noopener noreferrer"><Translate value="home.contactus"/></a></h4>
            <KSpace spaces={2}/>
        </KContainer>
    );
}
